import React, { useContext, useEffect, useState } from 'react';
import { Pressable, SafeAreaView, ScrollView, View } from 'react-native';
import { Text } from '@/components/Text';
import { useStoreActions, useStoreState } from '@/stores/store';
import { CustomCheckBox } from '@/components/SharedComponents/CustomCheckBox/';
import { fetchUserConsents } from '@/api/services/user';
import colors, { appTheme } from '@/config/colors';
import { connectWithEpa } from '@/utils/epaAuth';
import { EpaAuthContext } from '@/contexts/EpaAuthContext';

import { styles } from './styles';
import { Divider } from 'react-native-elements';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { Icon } from '@/components/Icon';
import theme from '@/config/theme';
import { DataTransferCard } from './DataTransferCard';
import { ConnectionStatusCard } from './ConnectionStatusCard';

export const EpaScreen = () => {
  const { storedUser, userConsents } = useStoreState((state) => ({
    storedUser: state.signedInUser,
    userConsents: state.userConsents,
  }));
  const { setUserInsuranceId } = useStoreActions((actions) => ({
    setUserInsuranceId: actions.setUserInsuranceId,
  }));

  let [isConnectEnabled, setConnectState] = useState<boolean>(false);
  let [showEpaConnectError, setShowEpaConnectError] = useState<boolean>(false);

  const authContext = useContext(EpaAuthContext);

  useEffect(() => {
    (async () => {
      await fetchUserConsents();
    })();
  }, []);

  let canWriteToEpa = false;
  for (let consent of userConsents) {
    if (consent.name === 'WriteToEpa') {
      canWriteToEpa = consent.givenConsent;
    }
  }

  const handleConnectWithEpa = async () => {
    await connectWithEpa(
      authContext,
      (insuranceId) => {
        setUserInsuranceId(insuranceId);
        fetchUserConsents();
      },

      () => {
        setShowEpaConnectError(true);
      },
    );
  };

  return (
    <SafeAreaView style={styles.fullHeight}>
      <ScrollView style={{ width: '100%' }}>
        <View style={styles.container}>
          <View style={[styles.cardContainer, { width: '70%' }]}>
            <View style={[styles.cardSection, styles.headingRow]}>
              <Icon
                name="fileText"
                size={20}
                color={appTheme.primary}
                style={styles.headingIcon}
              />
              <Text style={styles.heading}>Was ist die ePA?</Text>
            </View>
            <Divider color={colors.lightGrey} width={2} />
            <View style={styles.cardSection}>
              <Text style={styles.text}>
                Alle gesetzlich Versicherten können eine elektronische
                Patientenakte (ePA) von ihrer Krankenkasse erhalten, in der
                medizinische Befunde und weitere persönliche Gesundheitsdaten
                gespeichert werden. Sie können selbst bestimmen, ob und in
                welchem Umfang Sie die ePA nutzen möchten.
              </Text>
            </View>
            <Divider color={colors.lightGrey} width={2} />
            <View style={styles.cardSection}>
              <Text style={styles.text}>
                Sie haben hierbei auch die Möglichkeit, die Übungsdauer und
                Übungsergebnisse aus neolexon in Ihre ePA zu übertragen. Bei der
                automatischen Übertragung werden vergangene Übungsdaten jeden
                Tag um 22:00 Uhr in die ePA übertragen.
              </Text>
            </View>
          </View>

          <View style={{ width: '70%' }}>
            {storedUser.user?.idpInsuranceId ? (
              <>
                <ConnectionStatusCard />
                <DataTransferCard canWriteToEpa={canWriteToEpa} />
              </>
            ) : (
              <>
                <View style={styles.cardContainer}>
                  <View style={[styles.cardSection, styles.headingRow]}>
                    <Icon
                      name="shield"
                      size={20}
                      color={appTheme.primary}
                      style={styles.headingIcon}
                    />
                    <Text style={styles.heading}>Einwilligung</Text>
                  </View>
                  <Divider color={colors.lightGrey} width={2} />
                  <View
                    style={[
                      styles.cardSection,
                      { flexDirection: 'row', alignItems: 'flex-start' },
                    ]}
                  >
                    <CustomCheckBox
                      checked={isConnectEnabled}
                      onChecked={(value: boolean) => {
                        setConnectState(!value);
                      }}
                    />
                    <Pressable
                      style={{ flex: 1 }}
                      onPress={() => setConnectState(!isConnectEnabled)}
                    >
                      <Text style={styles.text}>
                        Ich willige ein, dass Übungsdauer und Übungsergebnisse
                        von neolexon in meiner persönlichen elektronischen
                        Patientenakte gespeichert werden. Ich kann meine
                        Einwilligung jederzeit widerrufen.
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View
                  style={[styles.hintRow, { marginTop: theme.spacings.lg }]}
                >
                  <Icon
                    name="exclamationCircle"
                    size={20}
                    color={colors.darkGrey}
                    style={styles.headingIcon}
                  />
                  <Text style={[styles.textSmall, { flex: 1 }]}>
                    Hinweis: hierzu müssen Sie sich einmalig mit Ihrer
                    GesundheitsID anmelden, um sich mit der ePA zu verbinden.
                  </Text>
                </View>

                <Button
                  style={{
                    marginTop: 20,
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  onPress={handleConnectWithEpa}
                  disabled={!isConnectEnabled}
                  label="Verbinden"
                />
                <Modal
                  isVisible={showEpaConnectError}
                  onClose={() => setShowEpaConnectError(false)}
                  title="Verbindung fehlgeschlagen"
                  cancelActionButton={
                    <Button
                      label="Abbrechen"
                      look="ghost"
                      onPress={() => setShowEpaConnectError(false)}
                    />
                  }
                  confirmActionButton={
                    <Button
                      label="Erneut versuchen"
                      look="primary"
                      onPress={() => {
                        setShowEpaConnectError(false);
                        handleConnectWithEpa();
                      }}
                    />
                  }
                >
                  <Text style={[styles.text, styles.textCenter]}>
                    Die Verbindung mit der GesundheitsID konnte nicht
                    hergestellt werden. Bitte versuchen Sie es erneut oder
                    kontaktieren Sie unseren Support, falls das Problem
                    weiterhin besteht.
                  </Text>
                </Modal>
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};
